import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';

import { ExternalEditInfoBanner } from '@xing-com/crate-entity-pages-common';
import { trackContentSwitcherFirstUse } from '@xing-com/crate-entity-pages-common/src/tracking';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useModulesContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-modules-context';

import { BRANDMANAGER_URL } from '../../config/urls';

import * as Styled from './navigation.styles';

type NavigationProps = {
  overwriteIndex?: number;
  navigationRef: (node: any) => void;
  navigationOffset: number;
  frameSize: {
    width: number;
    height: number;
  };
  navigationBarHeight: number;
};

export const Navigation: FC<NavigationProps> = ({
  overwriteIndex,
  navigationOffset,
  frameSize,
  navigationBarHeight,
  navigationRef,
}) => {
  const { $t } = useIntl();
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const { pageModulesByType } = useModulesContext();

  const [activeIndex, setActiveIndex] = useState(0);
  const [isSticky, setSticky] = useState(false);
  const [firstNavClick, setFirstNavClick] = useState(true);

  const { ref: inViewRef } = useInView({
    rootMargin: `-${frameSize.height}px 0px 0px 0px`,
    threshold: 1,
    onChange(_inView, { rootBounds, boundingClientRect }) {
      if (rootBounds) setSticky(boundingClientRect.y < rootBounds.y);
    },
  });

  const scrollToModule = (type: string) => {
    const moduleOffset =
      window.pageYOffset +
      document.getElementById(`module-${type}`)!.getBoundingClientRect().top;

    const margin = 180;

    setActiveIndex(pageModulesByType.indexOf(type) ?? 0);

    if (firstNavClick) {
      trackContentSwitcherFirstUse(type);
      setFirstNavClick(false);
    }

    window.scrollTo({
      top: moduleOffset - navigationOffset - margin,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (overwriteIndex !== undefined) {
      setActiveIndex(overwriteIndex);
    }
  }, [overwriteIndex]);

  const contentSwitchData = useMemo(() => {
    return pageModulesByType.map((pageModuleType) => ({
      copy: $t({
        id: 'EP_' + pageModuleType.toUpperCase() + '_NAVIGATION_TITLE',
      }),
      ['data-testid']: `EP-${pageModuleType.toUpperCase()}-NAVIGATION`,
      onClick: () => scrollToModule(pageModuleType),
    }));
  }, [pageModulesByType, $t]);

  const showBrandManagerBanner =
    isEditing && !pageContext.isPageDraft && pageContext.isCompanyPage;

  const navigationHeight =
    navigationBarHeight > 0 ? navigationBarHeight : 'inherit';
  const navigationWidth = frameSize.width > 0 ? frameSize.width : 'inherit';

  return (
    <Styled.NavigationWrapper $isEditing={isEditing}>
      <div className="navigationSentinal" ref={inViewRef} />
      <div style={{ height: navigationHeight }}>
        <Styled.Navigation
          $isSticky={isSticky}
          $isEditor={pageContext.isEditor}
          style={{ height: navigationHeight, width: navigationWidth }}
        >
          <Styled.NavigationContainer ref={navigationRef}>
            <Styled.ContentSwitcherContainer>
              <Styled.ContentSwitcher
                activeItemIndex={activeIndex}
                variant="filled"
                size="small"
                isFixed={isSticky}
                data={contentSwitchData}
                aria-label={$t({ id: 'EP_NAVIGATION_ARIA_LABEL' })}
              />
            </Styled.ContentSwitcherContainer>
          </Styled.NavigationContainer>
          <Styled.NavigationBorder />
        </Styled.Navigation>
      </div>

      {showBrandManagerBanner ? (
        <Styled.InfoBanner>
          <ExternalEditInfoBanner
            copyKey="EP_NAVIGATION_EDIT_HINT"
            ctaCopyKey="EP_NAVIGATION_EDIT_HINT_CTA"
            ctaUrl={BRANDMANAGER_URL(pageContext.pageSlug)}
            cyTestId="EDIT_HINT_NAVIGATION"
            ctaAvailableInMobile={false}
          />
        </Styled.InfoBanner>
      ) : null}
    </Styled.NavigationWrapper>
  );
};
